import DOMPurify from "dompurify";
import linkifyStr from "linkify-string";
import { escapeRegExp } from "@/src/utils/utils.js";

/**
 * Highlights search query matches in text with a mark tag
 * @param {string} text - The text to highlight
 * @param {string} searchQuery - The search query to highlight
 * @returns {string} - Sanitized HTML with highlighted matches
 */
export function highlightSearchText(text, searchQuery) {
  try {
    if (!text) {
      return "";
    }

    // Sanitize the text first to prevent XSS
    const sanitizedText = DOMPurify.sanitize(text, { ALLOWED_TAGS: [] });

    if (!searchQuery || searchQuery.trim() === "") {
      return sanitizedText;
    }

    const query = searchQuery.trim();
    const escapedQuery = escapeRegExp(query);
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    const highlighted = sanitizedText.replace(
      regex,
      '<mark class="search-highlight">$1</mark>',
    );

    // Sanitize again but allow the mark tag we just added
    return DOMPurify.sanitize(highlighted, {
      ALLOWED_TAGS: ["mark"],
      ALLOWED_ATTR: ["class"],
    });
  } catch (error) {
    console.error("Error highlighting search text:", error);
    return text || "";
  }
}

export function getTextFromHtml(html) {
  try {
    const parser = new DOMParser();
    const signatureBody = parser.parseFromString(html, "text/html");
    return signatureBody.body.textContent.trim();
  } catch (error) {
    console.error("An error occurred while getting text from HTML: ", error);
  }
}

export function formatLinksInsideString(str, text) {
  try {
    if (str === null || str === undefined || str === "") {
      return text || "No string";
    }

    const updatedStr = linkifyStr(str, {
      defaultProtocol: "https",
      target: "_blank",
      rel: "noopener noreferrer",
      className:
        "link_color no-underline hover:underline transition-all duration-300 ease-in-out",
      validate: {
        email: () => false,
      },
    });

    return DOMPurify.sanitize(updatedStr, {
      ADD_ATTR: ["target", "rel"],
    });
  } catch (error) {
    console.error(
      "An error occurred while formatting links inside String: ",
      error,
    );
    return text ? DOMPurify.sanitize(text) : DOMPurify.sanitize(str || "");
  }
}

export function getPageContext() {
  try {
    const body = document.body.cloneNode(true);
    const scripts = body.querySelectorAll("script, style, noscript");
    scripts.forEach((el) => el.remove());
    return body.innerText || body.textContent || "";
  } catch (error) {
    console.error("Error extracting page context:", error);
    return "";
  }
}

export function getObjectInfoFromURL(path) {
  try {
    const urlPath = path || window.location.pathname;
    const segments = urlPath.split('/').filter(segment => segment);
    
    const crmIndex = segments.indexOf('crm');
    if (crmIndex !== -1 && segments.length >= crmIndex + 3) {
      const objectType = segments[crmIndex + 1];
      const objectId = segments[crmIndex + 2];
      
      if (objectType && objectId && !isNaN(objectId)) {
        return {
          object_type: objectType,
          object_id: objectId
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error("Error extracting object info from URL:", error);
    return null;
  }
}
